type FieldMapping = {
  label: string;
  path: string[];
  transform?: (value: any) => string;
};

type ResourceFieldMappings = {
  sqs: FieldMapping[];
  vpcAzure: FieldMapping[];
  vpcAws: FieldMapping[];
};

const getNestedValue = (obj: any, path: string[]): any => {
  return path.reduce((current, key) => current?.[key], obj);
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

const parseJsonString = (value: any): string => {
  if (!value || value === '-') {
    return '-';
  }
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => {
          if (typeof item === 'object') {
            return JSON.stringify(item);
          }
          return String(item);
        })
        .join(', ');
    }
    return typeof parsed === 'object'
      ? JSON.stringify(parsed, null, 2)
      : String(parsed);
  } catch {
    return String(value);
  }
};

const formatVpcSubnets = (value: any): string => {
  if (!value || value === '-') {
    return '-';
  }
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (Array.isArray(parsed)) {
      return parsed
        .map((subnet) => {
          if (typeof subnet === 'object' && subnet !== null) {
            const name = subnet.name || 'N/A';
            const addressPrefix = subnet.address_prefix || 'N/A';
            return `${name} (${addressPrefix})`;
          }
          return String(subnet);
        })
        .join(', ');
    }
    return typeof parsed === 'object'
      ? JSON.stringify(parsed, null, 2)
      : String(parsed);
  } catch {
    return String(value);
  }
};

export const RESOURCE_FIELD_MAPPINGS: ResourceFieldMappings = {
  sqs: [
    {
      label: 'Cloud Location ID',
      path: ['cloudLocationId'],
    },
    {
      label: 'Cloud Resource URL',
      path: ['cloudResourceUrl'],
    },
    {
      label: 'Region',
      path: ['resourceInfo', 'parameters', 'region', 'value'],
    },
  ],
  vpcAzure: [
    {
      label: 'Resource Type',
      path: ['resourceType'],
    },
    {
      label: 'VPC ID',
      path: ['resourceInfo', 'parameters', 'vpcId', 'value'],
    },
    {
      label: 'VPC Subnets',
      path: ['resourceInfo', 'parameters', 'vpcSunbnets', 'value'],
      transform: formatVpcSubnets,
    },
    {
      label: 'VPC Location',
      path: ['resourceInfo', 'parameters', 'vpcLocation', 'value'],
    },
    {
      label: 'AKS Subnet ID',
      path: ['resourceInfo', 'parameters', 'aks_subnet_id', 'value'],
    },
    {
      label: 'NAT Gateway Public IP',
      path: ['resourceInfo', 'parameters', 'natGatewayPublicIp', 'value'],
    },
  ],
  vpcAws: [
    {
      label: 'Resource Type',
      path: ['resourceType'],
    },
    {
      label: 'VPC ID',
      path: ['resourceInfo', 'parameters', 'vpcId', 'value'],
    },
    {
      label: 'VPC Private Subnet IDs',
      path: ['resourceInfo', 'parameters', 'vpcPrivateSubnetIds', 'value'],
      transform: parseJsonString,
    },
    {
      label: 'VPC Public Subnet IDs',
      path: ['resourceInfo', 'parameters', 'vpcPublicSubnetIds', 'value'],
      transform: parseJsonString,
    },
    {
      label: 'VPC Default Security Group ID',
      path: [
        'resourceInfo',
        'parameters',
        'vpcDefaultSecurityGroupId',
        'value',
      ],
    },
    {
      label: 'API Gateway VPC Link ID',
      path: ['resourceInfo', 'parameters', 'apiGatewayVpcLinkId', 'value'],
    },
    {
      label: 'NAT Gateway Public IP',
      path: ['resourceInfo', 'parameters', 'natGatewayPublicIp', 'value'],
    },
  ],
};

export const extractResourceFields = (
  resource: any,
  mappings: FieldMapping[],
): Array<{ label: string; value: string }> => {
  if (!resource || !mappings) {
    return [];
  }

  return mappings.map((mapping) => {
    const rawValue = getNestedValue(resource, mapping.path);
    const value = mapping.transform
      ? mapping.transform(rawValue)
      : formatValue(rawValue);

    return {
      label: mapping.label,
      value,
    };
  });
};

export const extractResourceFieldsFromArray = (
  resources: any[],
  mappings: FieldMapping[],
): Array<Array<{ label: string; value: string }>> => {
  if (!Array.isArray(resources) || !mappings) {
    return [];
  }

  return resources.map((resource) => extractResourceFields(resource, mappings));
};

export const getVpcFieldMappings = (resourceType: string): FieldMapping[] => {
  if (resourceType === 'AZURE_VNET') {
    return RESOURCE_FIELD_MAPPINGS.vpcAzure;
  }
  if (resourceType === 'AWS_VPC') {
    return RESOURCE_FIELD_MAPPINGS.vpcAws;
  }
  return [];
};

export const extractVpcResourceFieldsFromArray = (
  resources: any[],
): Array<Array<{ label: string; value: string }>> => {
  if (!Array.isArray(resources)) {
    return [];
  }

  return resources.map((resource) => {
    const resourceType = resource?.resourceType;
    const mappings = getVpcFieldMappings(resourceType);
    return extractResourceFields(resource, mappings);
  });
};
